import { put, PutEffect, select, SelectEffect } from 'redux-saga/effects';
import cloneDeep from 'lodash.clonedeep';

import { RearrangeElementsOnTimelineAction, updateScribe } from 'js/actionCreators/scribeActions';
import { ExistingScribeModel } from 'js/types';

import { getActiveElements, getScribeById } from '../selectors';
import multiDragAwareReorder from '../sagaHelpers/multiDragAwareReorder';

export function* rearrangeElementsOnTimeline({
  scribeId,
  source,
  destination,
  draggableId,
  elementIds = [],
  sceneId
}: RearrangeElementsOnTimelineAction): Generator<
  SelectEffect | PutEffect,
  void,
  (ExistingScribeModel | undefined) | string[]
> {
  const activeScribe = (yield select(getScribeById, scribeId)) as ExistingScribeModel | undefined;
  const activeElements = (yield select(getActiveElements)) as string[];

  if (!activeScribe) {
    return;
  }

  const activeScene = activeScribe.scenes.find(scene => (sceneId === undefined ? scene.active : scene.id === sceneId));
  if (!activeScene) {
    if (sceneId !== undefined) {
      throw new Error(`Cannot reorder missing scene ${sceneId}`);
    }
    return;
  }

  const elementIdsToSort = elementIds.length ? elementIds : activeElements;
  const firstElementId = activeScene.elementIds[0];
  const startingCamera = activeScribe.elements.find(
    element => element.id === firstElementId && element.type === 'Camera'
  );
  if (startingCamera && (draggableId === startingCamera.id || elementIdsToSort.includes(startingCamera.id))) {
    console.error(`Cannot reorder starting camera in scene ${activeScene.id}`);
    return;
  }

  const elementIdsList = activeScene.elementIds.map(id => ({ id }));
  const updatedElements = multiDragAwareReorder(elementIdsList, source, destination, draggableId, elementIdsToSort);
  if (startingCamera && updatedElements[0]?.id !== startingCamera.id) {
    console.error(`Cannot reorder elements before starting camera in scene ${activeScene.id}`);
    return;
  }

  const newActiveScribe = cloneDeep(activeScribe);
  const newActiveScene = newActiveScribe.scenes.find(scene => scene.id === activeScene.id);
  if (!newActiveScene) {
    return;
  }

  newActiveScene.elementIds = updatedElements.map(element => {
    return element.id;
  });

  yield put(updateScribe(newActiveScribe));
}
